/**
 * Feedback Service for Thai Security Directory
 * Handles all feedback-related API calls
 */

import { apiClient } from './api';
import {
  Feedback,
  CreateFeedbackRequest,
  FeedbackListResponse,
  FeedbackCategory,
  FeedbackStatus,
  FeedbackStatusDisplay,
  FeedbackCategoryDisplay,
} from '../types/feedback';

class FeedbackService {
  private readonly baseURL = '/api/feedback';

  /**
   * Submit new feedback (GUEST or MEMBER)
   */
  async createFeedback(data: CreateFeedbackRequest): Promise<Feedback> {
    const response = await apiClient.post(`${this.baseURL}/`, data);
    return response.data;
  }

  /**
   * Get user's own feedbacks (MEMBER only)
   */
  async getMyFeedbacks(page = 1, perPage = 10): Promise<FeedbackListResponse> {
    const response = await apiClient.get(`${this.baseURL}/my-feedbacks`, {
      params: { page, per_page: perPage },
    });
    return response.data;
  }

  /**
   * Get feedback categories with labels
   */
  getFeedbackCategories(): FeedbackCategoryDisplay[] {
    return [
      {
        value: FeedbackCategory.BUG_REPORT,
        label: 'รายงานบัค',
        emoji: '🐛',
      },
      {
        value: FeedbackCategory.FEATURE_REQUEST,
        label: 'ขอฟีเจอร์ใหม่',
        emoji: '✨',
      },
      {
        value: FeedbackCategory.GENERAL_FEEDBACK,
        label: 'ข้อเสนอแนะทั่วไป',
        emoji: '💡',
      },
      {
        value: FeedbackCategory.COMPLAINT,
        label: 'ร้องเรียน',
        emoji: '😞',
      },
      {
        value: FeedbackCategory.COMPLIMENT,
        label: 'ชื่นชม',
        emoji: '👍',
      },
      {
        value: FeedbackCategory.OTHER,
        label: 'อื่นๆ',
        emoji: '💬',
      },
    ];
  }

  /**
   * Get category display label with emoji
   */
  getCategoryDisplay(category: FeedbackCategory): string {
    const categoryMap = this.getFeedbackCategories();
    const found = categoryMap.find((c) => c.value === category);
    return found ? `${found.emoji} ${found.label}` : category;
  }

  /**
   * Get status display with colors
   */
  getStatusDisplay(status: FeedbackStatus): FeedbackStatusDisplay {
    const statusMap: Record<FeedbackStatus, FeedbackStatusDisplay> = {
      [FeedbackStatus.NEW]: {
        text: 'ใหม่',
        color: 'yellow',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
      },
      [FeedbackStatus.READ]: {
        text: 'อ่านแล้ว',
        color: 'blue',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
      },
      [FeedbackStatus.IN_PROGRESS]: {
        text: 'กำลังดำเนินการ',
        color: 'orange',
        bgColor: 'bg-orange-100',
        textColor: 'text-orange-800',
      },
      [FeedbackStatus.RESOLVED]: {
        text: 'เสร็จสิ้น',
        color: 'green',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
      },
      [FeedbackStatus.CLOSED]: {
        text: 'ปิด',
        color: 'gray',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
      },
    };

    return statusMap[status] || statusMap[FeedbackStatus.NEW];
  }

  /**
   * Validate feedback form
   */
  validateFeedbackForm(data: CreateFeedbackRequest): {
    isValid: boolean;
    errors: Record<string, string>;
  } {
    const errors: Record<string, string> = {};

    // Subject validation
    if (!data.subject || data.subject.trim().length < 3) {
      errors.subject = 'หัวเรื่องต้องมีอย่างน้อย 3 ตัวอักษร';
    } else if (data.subject.length > 200) {
      errors.subject = 'หัวเรื่องต้องไม่เกิน 200 ตัวอักษร';
    }

    // Category validation
    if (!data.category) {
      errors.category = 'กรุณาเลือกประเภทความคิดเห็น';
    }

    // Message validation
    if (!data.message || data.message.trim().length < 10) {
      errors.message = 'ความคิดเห็นต้องมีอย่างน้อย 10 ตัวอักษร';
    }

    // Rating validation (optional)
    if (data.rating !== undefined) {
      if (data.rating < 1 || data.rating > 5) {
        errors.rating = 'คะแนนต้องอยู่ระหว่าง 1-5';
      }
    }

    // Email validation (optional)
    if (data.email && data.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.email)) {
        errors.email = 'รูปแบบอีเมลไม่ถูกต้อง';
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  }

  /**
   * Format timestamp to Thai date format
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    return date.toLocaleDateString('th-TH', options);
  }
}

// Export singleton instance
export const feedbackService = new FeedbackService();
